import {inject, Injectable} from '@angular/core';
import {Translation, TranslocoService} from '@jsverse/transloco';
import {firstValueFrom} from 'rxjs';
import {logger} from './logging.service';
import {PortalTheme} from '../types/portalLayoutSettings';

export const BRAND_TRANSLATION_LANGS = ['de', 'en'] as const;

// The texts a theme replaces live in the main translation files under `themes.<theme>`, with the
// same keys as the texts they replace. Returns them keyed like the originals.
export function themeOverrides(translation: Translation, theme: string): Record<string, unknown> {
    const prefix = `themes.${theme}.`;
    const overrides: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(translation)) {
        if (key.startsWith(prefix)) {
            overrides[key.slice(prefix.length)] = value;
        }
    }
    const nested = (translation['themes'] as Record<string, unknown> | undefined)?.[theme];
    if (nested && typeof nested === 'object') {
        Object.assign(overrides, flatten(nested as Record<string, unknown>));
    }
    return overrides;
}

function flatten(source: Record<string, unknown>, prefix = ''): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(source)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            Object.assign(result, flatten(value as Record<string, unknown>, prefix + key + '.'));
        } else {
            result[prefix + key] = value;
        }
    }
    return result;
}

// A theme keeps its own wording for some portal texts. They are merged over the standard
// translations once per page load, for every language the portal ships.
@Injectable({providedIn: 'root'})
export class BrandTranslationService {
    private readonly translocoService = inject(TranslocoService);
    private loadedFor: string | null = null;

    async applyOverrides(theme: PortalTheme): Promise<void> {
        if (!theme || this.loadedFor === theme) {
            return;
        }
        this.loadedFor = theme;
        await Promise.all(BRAND_TRANSLATION_LANGS.map((lang) => this.applyLanguage(theme, lang)));
    }

    private async applyLanguage(theme: string, lang: string): Promise<void> {
        try {
            const translation = await firstValueFrom(this.translocoService.selectTranslation(lang));
            const overrides = themeOverrides(translation, theme);
            if (Object.keys(overrides).length > 0) {
                this.translocoService.setTranslation(overrides, lang, {merge: true});
            }
        } catch (error) {
            logger.error(`Failed to apply the ${lang} texts of theme ${theme}`, error);
        }
    }
}
